import { CaretIcon, getIcon } from "@components/icon";
import {
    IFieldComponentProps,
    ISelectionChangeArgs,
    ISelectItem,
    ISelectPropsBase
} from "@components/inputs/select/Select.types";
import UserInitial from "@components/token/UserInitial";
import { Placement } from "@floating-ui/react-dom";
import { CustomerPermissionCodes } from "@odata/CompanyPermissionEnums";
import {
    CompanyEntity,
    CompanyRoleEntity,
    CompanyRolePermissionEntity,
    EntitySetName,
    IUserEntity,
    UserCompanyRoleEntity,
    UserEntity
} from "@odata/GeneratedEntityTypes";
import { UserStatusCode } from "@odata/GeneratedEnums";
import { WithOData, withOData } from "@odata/withOData";
import { CustomerGeneralRoleId, getUserNameInitials, transformUserSelectItems } from "@pages/admin/users/Users.utils";
import { CUSTOMER_GROUP, getUserSelectGroups, USER_GROUP } from "@pages/chats/Chats.utils";
import { composeRefHandlers, isDefined } from "@utils/general";
import { logger } from "@utils/log";
import { getOneFetch, isAbortException } from "@utils/oneFetch";
import { capitalize, universalPersonNameFormatter } from "@utils/string";
import i18next from "i18next";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { IconSize, LogicOperator, TextAlign } from "../../../enums";
import { TRecordAny } from "../../../global.types";
import { TableButtonsAction } from "../../../views/table/TableToolbar.utils";
import { IconButton } from "../../button";
import { BasicSelect, BasicSelectClean } from "./BasicSelect";

export interface IUserIconSelectProps extends WithOData, Omit<ISelectPropsBase, "ref"> {
    iconName?: string;
    isDisabled?: boolean;
    skippedUserId?: string;
    isActive?: boolean;
    selectRef?: React.RefObject<BasicSelectClean>;
    ref?: React.Ref<UserIconSelect>;
    isCustomer: boolean;
    allowEmpty?: boolean;
}

interface IState {
    items: ISelectItem[];
}

class UserIconSelect extends React.PureComponent<IUserIconSelectProps & WithTranslation, IState> {
    _refSelect = React.createRef<BasicSelectClean>();
    _refButton = React.createRef<HTMLButtonElement>();
    _fetch = getOneFetch();

    static contextType = AppContext;

    state: IState = {
        items: undefined
    }

    public open = (): void => {
        this._refButton.current?.focus();
        this._refSelect.current?.open();
    };

    get isOpened() {
        return this._refSelect.current?.isOpen();
    }

    handleIconActivate = (e: React.MouseEvent) => {
        if (!this.state.items) {
            e.preventDefault();
            this.load();
        }
    }

    handleOpen = () => {
        if (!this.state.items) {
            this.load();
        }
    }

    async load() {
        let users: IUserEntity[] = [];

        try {
            const query = this.props.oData.fromPath(EntitySetName.Users).query().expand(UserEntity.GeneralRole).orderBy(UserEntity.LastName);
            if (this.props.isCustomer) {
                query.filter(`GeneralRole/Id ne ${CustomerGeneralRoleId}`);
            }

            if (isDefined(this.props.skippedUserId)) {
                query.filter(`Id ne ${this.props.skippedUserId}`, LogicOperator.And);
            }

            query.filter(`${UserEntity.StatusCode} in ('${UserStatusCode.Invited}', '${UserStatusCode.Inactive}') eq false`, LogicOperator.And);
            query.filter(`${UserEntity.Id} gt 0`, LogicOperator.And);

            const companyId = this.context.getCompanyId();
            if (companyId) {
                query.filter(`${UserEntity.CompanyRoles}/any(d: d/${UserCompanyRoleEntity.Company}/${CompanyEntity.Id} eq ${companyId})`, LogicOperator.And);
            }


            const permissionCode = CustomerPermissionCodes.ChatInteractWithAccountant;

            query.filter(`(GeneralRole/Id ne ${CustomerGeneralRoleId} OR (
                ${UserEntity.CompanyRoles}/any(cr:
                cr/${UserCompanyRoleEntity.Company}/${CompanyEntity.Id} eq ${companyId}
                AND
                cr/${UserCompanyRoleEntity.CompanyRole}/${CompanyRoleEntity.CompanyRolePermissions}/any(p:
                            p/${CompanyRolePermissionEntity.CompanyPermissionCode} eq '${permissionCode}' AND 
                             p/${CompanyRolePermissionEntity.IsEnabled} eq true
                            ))
                         ))`, LogicOperator.And);

            const response = await query.fetchData(this._fetch.fetch);
            users = response.value;
        } catch (error) {
            if (isAbortException(error)) {
                return;
            }

            logger.error("error in fetch users", error);
        }

        this.setState({
            items: transformUserSelectItems(this.convertUsersToItems(users))
        }, () => {
            this.open();
        });
    }

    convertUsersToItems = (users: IUserEntity[]) => {
        return users?.map(user => {
            return {
                id: user.Id,
                label: universalPersonNameFormatter(user),
                groupId: user.GeneralRole?.Id === CustomerGeneralRoleId ? CUSTOMER_GROUP : USER_GROUP,
                tabularData: [user.Name, user.GeneralRole?.GeneralRoleName],
                additionalData: {
                    FirstName: user.FirstName,
                    LastName: user.LastName
                }
            };
        }).sort((user1: ISelectItem, user2: TRecordAny) => {
            if (user1.groupId === user2.groupId) {
                return 0;
            }

            return user1.groupId === CUSTOMER_GROUP ? -1 : 1;
        });
    }

    renderFieldComponent = (props: IFieldComponentProps): React.ReactElement => {
        const Icon = getIcon(this.props.iconName);

        return (
            <IconButton
                hotspotId={TableButtonsAction.Add}
                isTransparent
                title={capitalize(i18next.t("Common:Form.Chat"))}
                onClick={this.props.isActive ? null : props.onIconClick}
                isActive={props.isOpen || this.props.isActive}
                isDisabled={this.props.isDisabled}
                passRef={composeRefHandlers(this._refButton, props.openerRef as React.Ref<HTMLButtonElement>)}>
                <Icon/>
            </IconButton>
        );
    };

    handleChange = (args?: ISelectionChangeArgs) => {
        args.triggerAdditionalTasks && this.props.onChange?.(args);
    }

    createNameMark = (): React.ReactElement => {
        if (this.props.value) {
            const item = this.state.items?.find(item => {
                return item?.id === this.props.value;
            });
            if (item) {
                const name = universalPersonNameFormatter(item?.additionalData);
                const shortCut = getUserNameInitials(item?.additionalData);

                return (
                    <UserInitial title={shortCut} tooltip={name} style={{ marginRight: "8px" }} isBig={false}/>
                );
            }
        }

        return null;
    }


    renderContent = (): React.ReactElement => {

        let sharedProps: ISelectPropsBase = {
            onChange: this.handleChange,
            closeOnSelection: true,
            openOnClick: true
        };

        if (this.props.iconName) {
            sharedProps = {
                ...sharedProps,
                fieldComponent: this.renderFieldComponent,
                placement: "bottom" as Placement,
                displayArrow: true,
                inputIsReadOnly: true,
                isSharpLeft: true,
                isSharpRight: true,
                isIconSelect: true,
                showSearchBoxInMenu: true,
                onIconActivate: this.handleIconActivate
            };
        } else {
            sharedProps = {
                ...sharedProps,
                onOpen: this.handleOpen,
                contentBefore: this.createNameMark(),
                inputIcon: <CaretIcon width={IconSize.M}/>
            };
        }

        return (
            <BasicSelect
                columns={[{ id: "Name" }, { id: "GeneralRole", textAlign: TextAlign.Right }]}
                noRecordText={this.props.allowEmpty ? i18next.t("Chat:Table.FilterEmpty") : null}
                {...this.props}
                groups={this.props.isCustomer ? null : getUserSelectGroups()}
                isDisabled={this.props.isDisabled}
                ref={composeRefHandlers(this._refSelect, this.props.selectRef)}
                {...sharedProps}
                items={this.state.items}
            />);
    };

    render() {
        return this.renderContent();
    }
}

export default (withTranslation(["Common", "Users"], { withRef: true })(withOData(UserIconSelect)));
